import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Portal,
  Snackbar,
  Paper,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddIcon from "@mui/icons-material/Add";
import { Search } from "../../UI/Search/Search";
import ProgressBar from "../../UI/ProgressBar";
import {
  useGetPromotionInviteByIdQuery,
  useSyncInviteEmployeesMutation,
  useSendPromotionInviteMutation,
  useCancelPromotionInviteMutation,
  useCompletePromotionInviteMutation,
  useDeletePromotionInviteMutation,
  useUpdatePromotionEmployeeStatusMutation,
  useRemindPromotionInviteMutation,
  usePreviewPromotionInviteEmailMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { debounce } from "../../../utils";
import { useAlert } from "../../../hooks/useAlert";
import {
  PROMOTION_SIDE_ACTIONS,
  PROMOTION_STATUSES,
  canCancelPromotionInvite,
  canCompletePromotionInvite,
  getPromotionMarkAsActions,
  hasPromotionEventElapsed,
  getPromotionRowActions,
  isPromotionMarkAsActionEnabled,
  promotionCompleteDisabledReason,
  promotionStatusLabel,
} from "../../../constants/promotionStatuses";
import {
  getPromotionSelectionModel,
  isPromotionRowSelectable,
  nextIdsAfterHeaderToggle,
  nextIdsAfterRowToggle,
} from "./promotionSelection";
import {
  formatPromotionEventTime,
  formatUtcEventDate,
} from "./promotionEventDateTime";
import {
  promotionMarkAsResultAlert,
  runSequentialMarkAsUpdates,
} from "./promotionMarkAs";
import { getMissingInviteSendFields } from "./promotionInviteFields";
import { getDescriptionText } from "./quillDescriptionLimit";
import {
  historyListTabForInvite,
  historyTabLabel,
  readHistoryListFilters,
  writeHistoryListSearch,
} from "./promotionHistoryColumns";
import { BreadcrumbAdmin } from "../../UI/BreadcrumbAdmin";
import { AddEmployeesToEventModal } from "./AddEmployeesToEventModal";
import { RemoveEmployeesConfirmModal } from "./RemoveEmployeesConfirmModal";
import { SendInviteConfirmModal } from "./SendInviteConfirmModal";
import { EditPromotionInviteModal } from "./EditPromotionInviteModal";
import { CancelEventConfirmModal } from "./CancelEventConfirmModal";
import { CompleteEventConfirmModal } from "./CompleteEventConfirmModal";
import { DeleteEventConfirmModal } from "./DeleteEventConfirmModal";
import { RejectInviteReasonModal } from "./RejectInviteReasonModal";
import {
  RejectionCommentModal,
  buildRejectionComment,
  employeeHasRejectionComment,
} from "./RejectionCommentModal";
import { EmailTemplatePreviewModal } from "./EmailTemplatePreviewModal";
import { isDevClient } from "../../../utils/apiBaseUrl";
import "./Promotions.css";

const STATUS_PILL_CLASS = {
  CONTACT_PENDING: "promo-pill--pending",
  ADDED_TO_EVENT: "promo-pill--event",
  INVITE_SENT: "promo-pill--sent",
  INVITE_ACCEPTED: "promo-pill--accepted",
  INVITE_REJECTED: "promo-pill--rejected",
  PROMOTION_COMPLETED: "promo-pill--completed",
};

const EVENT_STATUS_PILL_CLASS = {
  PROMOTION_CREATED: "promo-pill--event",
  INVITE_SENT: "promo-pill--sent",
  COMPLETED: "promo-pill--completed",
  CLOSED: "promo-pill--canceled",
};

const DESCRIPTION_PREVIEW_LIMIT = 35;

/** Invite Sent event details always show these three groups, including zeros. */
const INVITE_SENT_STATUS_GROUP_ORDER = [
  PROMOTION_STATUSES.INVITE_SENT,
  PROMOTION_STATUSES.INVITE_ACCEPTED,
  PROMOTION_STATUSES.INVITE_REJECTED,
];

/** Completed events always show Completed + Rejected, including zeros. */
const CLOSED_STATUS_GROUP_ORDER = [
  PROMOTION_STATUSES.PROMOTION_COMPLETED,
  PROMOTION_STATUSES.INVITE_REJECTED,
];

const CLOSED_STATUS_GROUP_LABELS = {
  [PROMOTION_STATUSES.PROMOTION_COMPLETED]: "Promotion Completed",
  [PROMOTION_STATUSES.INVITE_REJECTED]: "Rejected",
};

const statusGroupLabel = (status, { closed = false } = {}) =>
  closed
    ? CLOSED_STATUS_GROUP_LABELS[status] || promotionStatusLabel(status)
    : promotionStatusLabel(status);

/** Rejected employees have no actions while the event is still Invite Sent. */
const isSelectableOnInviteSentEvent = (status) =>
  status !== PROMOTION_STATUSES.INVITE_REJECTED;

const ACCORDION_HEADER_HEIGHT = 48;
const ACCORDION_END_PADDING = 16;
const ROSTER_BOTTOM_GAP = 16;

export const groupEmployeesByStatus = (
  rows = [],
  { closed = false, cancelled = false } = {}
) => {
  const recordOnly = closed || cancelled;
  const completedRecord = closed && !cancelled;
  const order = completedRecord
    ? CLOSED_STATUS_GROUP_ORDER
    : INVITE_SENT_STATUS_GROUP_ORDER;
  const buckets = new Map(order.map((status) => [status, []]));
  const extras = new Map();

  for (const row of rows) {
    const status = row?.status || "UNKNOWN";
    if (buckets.has(status)) {
      buckets.get(status).push(row);
      continue;
    }
    if (recordOnly) continue;
    if (!extras.has(status)) extras.set(status, []);
    extras.get(status).push(row);
  }

  const groups = order.map((status) => ({
    status,
    label: statusGroupLabel(status, { closed: completedRecord }),
    rows: buckets.get(status) || [],
  }));

  if (!recordOnly) {
    for (const [status, list] of extras) {
      if (!list?.length) continue;
      groups.push({
        status,
        label: list[0]?.statusLabel || promotionStatusLabel(status),
        rows: list,
      });
    }
  }

  return groups;
};

const RejectionCommentButton = ({ onClick }) => (
  <IconButton
    size="small"
    aria-label="View rejection comment"
    onClick={onClick}
    sx={{ color: "var(--pblue)" }}
  >
    <ChatBubbleOutlineIcon fontSize="small" />
  </IconButton>
);

const descriptionPreview = (html) => {
  const text = getDescriptionText(html);
  if (!text) return "—";
  if (text.length <= DESCRIPTION_PREVIEW_LIMIT) return text;
  return `${text.slice(0, DESCRIPTION_PREVIEW_LIMIT)}…`;
};

const formatShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${mon} ${year}`;
};

const SELECT_CHECKBOX_SX = {
  color: "var(--grey400, #98a2b3)",
  "&.Mui-checked": { color: "var(--pblue)" },
  "&.MuiCheckbox-indeterminate": { color: "var(--pblue)" },
  "&.Mui-disabled": { color: "var(--grey300, #d0d5dd)" },
};

const matchesEmployeeSearch = (row, search) => {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return true;
  return [
    row.employee?.fname,
    row.employee?.lname,
    row.employee?.name,
    row.employee?.email,
    row.company?.name,
  ].some((value) => String(value || "").toLowerCase().includes(q));
};

export const PromotionEventDetail = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listSearch = location.state?.listSearch || "";
  const manageEventsPath = `/super-admin/promotions/history${listSearch}`;
  const { displayAlert } = useAlert();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [addEmployeesOpen, setAddEmployeesOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [sendInviteOpen, setSendInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [missingSendFields, setMissingSendFields] = useState([]);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuEmployee, setMenuEmployee] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [pendingMarkAsAction, setPendingMarkAsAction] = useState(null);
  const [markAsMenuAnchor, setMarkAsMenuAnchor] = useState(null);
  const [viewReasonOpen, setViewReasonOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState(null);
  const [invitePreview, setInvitePreview] = useState(null);
  const [expandedStatus, setExpandedStatus] = useState(null);
  const tableWrapRef = useRef(null);
  const defaultOpenedFor = useRef(null);
  const [toastBox, setToastBox] = useState(null);
  const [rosterScrollMax, setRosterScrollMax] = useState(null);
  const [accordionScrollMax, setAccordionScrollMax] = useState(null);

  const [syncEmployees, { isLoading: isRemoving }] =
    useSyncInviteEmployeesMutation();
  const [sendInvite, { isLoading: isSending }] =
    useSendPromotionInviteMutation();
  const [cancelInvite, { isLoading: isCancelling }] =
    useCancelPromotionInviteMutation();
  const [deleteInvite, { isLoading: isDeleting }] =
    useDeletePromotionInviteMutation();
  const [updateEmployeeStatus, { isLoading: isUpdatingStatus }] =
    useUpdatePromotionEmployeeStatusMutation();
  const [completeInvite, { isLoading: isCompleting }] =
    useCompletePromotionInviteMutation();
  const [remindEmployee, { isLoading: isReminding }] =
    useRemindPromotionInviteMutation();
  const [previewInviteEmail, { isLoading: isPreviewingInvite }] =
    usePreviewPromotionInviteEmailMutation();

  const setDebouncedSearch = useMemo(
    () => debounce((value) => setSearchQuery(value), 400),
    []
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetPromotionInviteByIdQuery(
      { inviteId },
      { skip: !inviteId, refetchOnMountOrArgChange: true }
    );

  const invite = data?.data?.invite;
  const detailTab = historyListTabForInvite(invite);
  const detailTabLabel = historyTabLabel(detailTab);
  const eventTabPath = `/super-admin/promotions/history${writeHistoryListSearch({
    ...readHistoryListFilters(new URLSearchParams(listSearch.replace(/^\?/, ""))),
    tab: detailTab,
  })}`;
  const allEmployees = useMemo(
    () => data?.data?.employees || [],
    [data?.data?.employees]
  );
  // Rejected employees are released from the event and kept only as a record.
  // They do not block cancelling an empty invite after the event time.
  const assignedEmployeeCount = useMemo(
    () => allEmployees.filter((row) => !row.isRosterSnapshot).length,
    [allEmployees]
  );
  const employees = useMemo(
    () => allEmployees.filter((row) => matchesEmployeeSearch(row, searchQuery)),
    [allEmployees, searchQuery]
  );
  const inviteRecipientEmails = useMemo(
    () =>
      allEmployees
        .map((row) => row.employee?.email)
        .filter(Boolean),
    [allEmployees]
  );
  const cancelRecipientEmails = useMemo(
    () =>
      allEmployees
        .filter((row) =>
          ["INVITE_SENT", "INVITE_ACCEPTED"].includes(row.status)
        )
        .map((row) => row.employee?.email)
        .filter(Boolean),
    [allEmployees]
  );
  const isInviteSent = invite?.status === "INVITE_SENT";
  const isCancelled = invite?.status === "CLOSED";
  const isClosed = isCancelled || invite?.status === "COMPLETED";
  const isCreated = !isInviteSent && !isClosed;
  const canCancelEvent = canCancelPromotionInvite({
    ...invite,
    employeeCount: assignedEmployeeCount,
  });
  const isLocked = isInviteSent || isClosed;
  const showResponseColumns = isInviteSent || isClosed;
  const closedGroupOrder = isCancelled
    ? INVITE_SENT_STATUS_GROUP_ORDER
    : CLOSED_STATUS_GROUP_ORDER;
  const closedVisibleTotal = useMemo(() => {
    if (!isClosed) return 0;
    return allEmployees.filter((row) =>
      closedGroupOrder.includes(row.status)
    ).length;
  }, [isClosed, allEmployees, closedGroupOrder]);

  const closedVisibleFiltered = useMemo(() => {
    if (!isClosed) return 0;
    return employees.filter((row) => closedGroupOrder.includes(row.status))
      .length;
  }, [isClosed, employees, closedGroupOrder]);

  const employeeGroups = useMemo(
    () =>
      showResponseColumns
        ? groupEmployeesByStatus(employees, {
            closed: isClosed && !isCancelled,
            cancelled: isCancelled,
          })
        : [],
    [showResponseColumns, employees, isClosed, isCancelled]
  );

  useEffect(() => {
    if (!showResponseColumns || !employeeGroups.length) return;
    if (!invite || String(invite._id) !== String(inviteId)) return;

    if (String(searchQuery || "").trim()) {
      const match = employeeGroups.find((group) => group.rows.length > 0);
      setExpandedStatus(match?.status || null);
      return;
    }

    if (defaultOpenedFor.current === String(inviteId)) return;
    defaultOpenedFor.current = String(inviteId);
    setExpandedStatus(employeeGroups[0].status);
  }, [showResponseColumns, employeeGroups, invite, inviteId, searchQuery]);

  const toggleStatusGroup = useCallback((status) => {
    setExpandedStatus((prev) => (prev === status ? null : status));
  }, []);

  const expandedGroupIndex = employeeGroups.findIndex(
    (group) => group.status === expandedStatus
  );
  const pinFollowingHeaders = accordionScrollMax != null;

  const completeInviteModel = {
    ...invite,
    employeeCount: assignedEmployeeCount,
  };
  const canCompleteEvent = canCompletePromotionInvite(completeInviteModel);
  const completeDisabledReason =
    promotionCompleteDisabledReason(completeInviteModel);
  const selectionModel = useMemo(() => {
    const rows = isInviteSent
      ? employees.filter((row) => isSelectableOnInviteSentEvent(row.status))
      : employees;
    return getPromotionSelectionModel(rows, selectedIds);
  }, [employees, isInviteSent, selectedIds]);
  const selectedCount = selectionModel.visibleSelectedIds.length;
  const selectionToastHidden =
    addEmployeesOpen ||
    removeConfirmOpen ||
    sendInviteOpen ||
    editOpen ||
    cancelOpen ||
    completeOpen ||
    deleteOpen ||
    rejectOpen ||
    viewReasonOpen ||
    Boolean(invitePreview) ||
    isPreviewingInvite;
  const showEventToast = Boolean(invite) && !isClosed;

  useLayoutEffect(() => {
    if (!isCreated) {
      setRosterScrollMax(null);
      return undefined;
    }

    const update = () => {
      const wrap = tableWrapRef.current;
      if (!wrap) return;
      const scroller = wrap.closest(".main__wrapper");
      const scrollerBottom =
        scroller?.getBoundingClientRect().bottom ?? window.innerHeight;
      const viewBottom = Math.min(window.innerHeight, scrollerBottom);
      const top = wrap.getBoundingClientRect().top;
      const next = Math.max(
        160,
        Math.floor(viewBottom - top - ROSTER_BOTTOM_GAP)
      );
      setRosterScrollMax((prev) => (prev === next ? prev : next));
    };

    update();
    const frame = window.requestAnimationFrame(update);
    const observed = tableWrapRef.current?.closest(".promo-event-detail");
    const scroller = tableWrapRef.current?.closest(".main__wrapper");
    const observer = new ResizeObserver(update);
    if (observed) observer.observe(observed);
    if (scroller) observer.observe(scroller);
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [isCreated, employees.length, isLoading]);

  useLayoutEffect(() => {
    if (!showResponseColumns || expandedGroupIndex < 0) {
      setAccordionScrollMax(null);
      return undefined;
    }

    const update = () => {
      const root = tableWrapRef.current;
      if (!root) return;
      const headers = [
        ...root.querySelectorAll(".promo-event-detail__status-group"),
      ];
      const openHeader = headers[expandedGroupIndex];
      const openButton = openHeader?.querySelector(
        ".promo-event-detail__status-group-btn"
      );
      if (!openButton) return;

      const scroller = root.closest(".main__wrapper");
      const scrollerRect = scroller?.getBoundingClientRect();
      const viewTop = scrollerRect?.top ?? 0;
      const viewBottom = Math.min(
        window.innerHeight,
        scrollerRect?.bottom ?? window.innerHeight
      );
      const headerBottom = openButton.getBoundingClientRect().bottom;
      if (headerBottom <= viewTop || headerBottom >= viewBottom) return;

      const followingCount = Math.max(
        0,
        headers.length - expandedGroupIndex - 1
      );
      const available = Math.max(
        0,
        Math.floor(
          viewBottom -
            ACCORDION_END_PADDING -
            headerBottom -
            followingCount * ACCORDION_HEADER_HEIGHT
        )
      );
      const contentHeight =
        openHeader.nextElementSibling?.querySelector(
          ".promo-event-detail__status-inner-table"
        )?.offsetHeight ?? 0;
      const nextMax = contentHeight > available ? available : null;
      setAccordionScrollMax((prev) => (prev === nextMax ? prev : nextMax));
    };

    update();
    const frame = window.requestAnimationFrame(update);
    const settled = window.setTimeout(update, 50);
    const scroller = tableWrapRef.current?.closest(".main__wrapper");
    const observed = tableWrapRef.current?.closest(".promo-event-detail");
    const observer = new ResizeObserver(() => update());
    if (observed) observer.observe(observed);
    if (scroller) observer.observe(scroller);
    scroller?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settled);
      observer.disconnect();
      scroller?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [
    showResponseColumns,
    expandedGroupIndex,
    employeeGroups,
    employees,
  ]);

  const markAsEventContext = useMemo(
    () => ({
      eventDate: invite?.eventDate,
      hasEventTime: invite?.hasEventTime !== false,
    }),
    [invite?.eventDate, invite?.hasEventTime]
  );
  const markAsActions = useMemo(
    () =>
      isInviteSent && selectedCount
        ? getPromotionMarkAsActions(selectionModel.anchorStatus).filter(
            (action) =>
              isPromotionMarkAsActionEnabled(action, markAsEventContext)
          )
        : [],
    [
      isInviteSent,
      selectedCount,
      selectionModel.anchorStatus,
      markAsEventContext,
    ]
  );
  const canMarkAs =
    isInviteSent &&
    selectedCount > 0 &&
    markAsActions.length > 0 &&
    !isUpdatingStatus;

  const updateToastBox = useCallback(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const table = wrap.querySelector(".promotions-table") || wrap;
    const rect = table.getBoundingClientRect();
    setToastBox({
      center: rect.left + rect.width / 2,
      minWidth: rect.width * 0.6,
      maxWidth: rect.width * 0.85,
    });
  }, []);

  useLayoutEffect(() => {
    updateToastBox();
    const el = tableWrapRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(updateToastBox);
    observer.observe(el);
    window.addEventListener("resize", updateToastBox);
    window.addEventListener("scroll", updateToastBox, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateToastBox);
      window.removeEventListener("scroll", updateToastBox, true);
    };
  }, [updateToastBox, showEventToast, employees.length]);

  useEffect(() => {
    setSelectedIds([]);
    setMarkAsMenuAnchor(null);
    setPendingMarkAsAction(null);
  }, [inviteId, invite?.status]);

  useEffect(() => {
    if (selectedCount) return;
    setMarkAsMenuAnchor(null);
    setPendingMarkAsAction(null);
  }, [selectedCount]);

  const handleSelectAllMatching = () => {
    setSelectedIds(nextIdsAfterHeaderToggle(selectionModel));
  };

  const toggleOne = (row, checked) => {
    setSelectedIds(nextIdsAfterRowToggle(row, checked, selectionModel));
  };

  const requestRemoveEmployees = (ids) => {
    if (!ids?.length) return;
    setPendingRemoveIds(ids);
    setRemoveConfirmOpen(true);
  };

  const confirmRemoveEmployees = async () => {
    if (!pendingRemoveIds.length) return;
    try {
      await syncEmployees({
        inviteId,
        promotionIds: pendingRemoveIds,
        mode: "remove",
      }).unwrap();
      displayAlert({
        open: true,
        message: "Employees removed from event",
        type: "success",
        timeout: "2500",
      });
      setSelectedIds((prev) =>
        prev.filter((id) => !pendingRemoveIds.includes(id))
      );
      setRemoveConfirmOpen(false);
      setPendingRemoveIds([]);
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to remove employees",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleSendInviteClick = () => {
    if (isLocked) {
      displayAlert({
        open: true,
        message: "Invites have already been sent for this event",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    if (!allEmployees.length) {
      displayAlert({
        open: true,
        message: "Add at least one employee before sending invites",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    const missing = getMissingInviteSendFields(invite);
    if (missing.length) {
      setMissingSendFields(missing);
      setEditOpen(true);
      return;
    }
    setSendInviteOpen(true);
  };

  const confirmSendInvite = async () => {
    try {
      const result = await sendInvite({ inviteId }).unwrap();
      displayAlert({
        open: true,
        message:
          result?.message ||
          `Invites sent to ${
            result?.data?.recipientCount || allEmployees.length
          } employees`,
        type: "success",
        timeout: "2500",
      });
      setSendInviteOpen(false);
      setSelectedIds([]);
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to send invites",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const closeHeaderMenu = () => setHeaderMenuAnchor(null);

  const handleHeaderEdit = () => {
    closeHeaderMenu();
    setMissingSendFields([]);
    setEditOpen(true);
  };

  const handleHeaderViewEvent = async () => {
    closeHeaderMenu();
    try {
      const result = await previewInviteEmail({ inviteId }).unwrap();
      setInvitePreview({
        subject: result?.data?.preview?.subject,
        html: result?.data?.preview?.html,
      });
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to preview invite email",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleHeaderCancel = () => {
    closeHeaderMenu();
    if (
      !canCancelPromotionInvite({
        ...invite,
        employeeCount: assignedEmployeeCount,
      })
    ) {
      return;
    }
    setCancelOpen(true);
  };

  const handleHeaderDelete = () => {
    closeHeaderMenu();
    setDeleteOpen(true);
  };

  const confirmCancelEvent = async () => {
    try {
      await cancelInvite({ inviteId }).unwrap();
      displayAlert({
        open: true,
        message: "Event cancelled",
        type: "success",
        timeout: "2500",
      });
      setCancelOpen(false);
      navigate("/super-admin/promotions/history");
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to cancel event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const confirmDeleteEvent = async () => {
    try {
      await deleteInvite({ inviteId }).unwrap();
      displayAlert({
        open: true,
        message: "Event deleted",
        type: "success",
        timeout: "2500",
      });
      setDeleteOpen(false);
      navigate("/super-admin/promotions/history");
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to delete event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const closeEmployeeMenu = () => {
    setMenuAnchor(null);
    setMenuEmployee(null);
  };

  const openEmployeeMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuEmployee(row);
  };

  const openRejectionComment = (row) => {
    setRejectionComment(buildRejectionComment(row));
    setViewReasonOpen(true);
  };

  const applyEmployeeStatus = async (target, status, reason) => {
    if (!target?._id) return;
    try {
      const result = await updateEmployeeStatus({
        promotionId: target._id,
        status,
        reason,
      }).unwrap();
      displayAlert({
        open: true,
        message: result?.message || "Employee status updated",
        type: "success",
        timeout: "2500",
      });
      setSelectedIds((prev) => prev.filter((id) => id !== target._id));
      refetch();
      return true;
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to update status",
        type: "error",
        timeout: "3000",
      });
      return false;
    }
  };

  const handleRowAction = async (action) => {
    const target = menuEmployee;
    closeEmployeeMenu();
    if (!target || !action) return;

    if (action.type === "side") {
      if (action.key === PROMOTION_SIDE_ACTIONS.SEND_REMINDER) {
        const recipient = target.employee?.email;
        if (isDevClient) {
          const listed = recipient || "(no email)";
          const confirmed = window.confirm(
            `Warning: An email will be sent in the development environment.\n\nSending is blocked unless ENABLE_DEV_EMAILS is explicitly enabled on the server.\n\nRecipients:\n- ${listed}`
          );
          if (!confirmed) return;
        }
        try {
          const result = await remindEmployee({
            promotionId: target._id,
          }).unwrap();
          displayAlert({
            open: true,
            message: result?.message || "Reminder sent",
            type: "success",
            timeout: "2500",
          });
        } catch (err) {
          displayAlert({
            open: true,
            message:
              err?.data?.message || err?.message || "Unable to send reminder",
            type: "error",
            timeout: "3000",
          });
        }
      }
      return;
    }

    if (!isPromotionMarkAsActionEnabled(action, markAsEventContext)) {
      displayAlert({
        open: true,
        message: "Promotion Completed can be set after the event time",
        type: "info",
        timeout: "2500",
      });
      return;
    }

    if (action.requiresReason) {
      setPendingMarkAsAction(null);
      setRejectTarget(target);
      setRejectOpen(true);
      return;
    }

    applyEmployeeStatus(target, action.key);
  };

  const closeMarkAsMenu = () => setMarkAsMenuAnchor(null);

  const applyMarkAsStatus = async (action, reason) => {
    const ids = selectionModel.visibleSelectedIds;
    if (!action?.key || !ids.length) return false;
    const result = await runSequentialMarkAsUpdates(ids, (promotionId) =>
      updateEmployeeStatus({
        promotionId,
        status: action.key,
        reason,
      }).unwrap()
    );
    const alert = promotionMarkAsResultAlert({
      ...result,
      actionLabel: action.label,
    });
    displayAlert({
      open: true,
      message: alert.message,
      type: alert.type,
      timeout: alert.timeout,
    });
    if (result.succeededIds?.length) {
      const updated = new Set(result.succeededIds);
      setSelectedIds((prev) => prev.filter((id) => !updated.has(id)));
      refetch();
    }
    return alert.ok;
  };

  const requestMarkAs = (action) => {
    closeMarkAsMenu();
    if (!action || !selectedCount || isUpdatingStatus) return;
    if (!isPromotionMarkAsActionEnabled(action, markAsEventContext)) return;

    if (action.requiresReason) {
      setRejectTarget(null);
      setPendingMarkAsAction(action);
      setRejectOpen(true);
      return;
    }
    applyMarkAsStatus(action);
  };

  const confirmRejectInvite = async (reason) => {
    if (pendingMarkAsAction) {
      const ok = await applyMarkAsStatus(pendingMarkAsAction, reason);
      if (ok) {
        setRejectOpen(false);
        setPendingMarkAsAction(null);
      }
      return;
    }
    if (!rejectTarget?._id) return;
    const ok = await applyEmployeeStatus(
      rejectTarget,
      PROMOTION_STATUSES.INVITE_REJECTED,
      reason
    );
    if (ok) {
      setRejectOpen(false);
      setRejectTarget(null);
    }
  };

  const handleMarkAsCompleted = () => {
    if (!canCompleteEvent) {
      displayAlert({
        open: true,
        message:
          completeDisabledReason ||
          "Event cannot be marked completed right now",
        type: "info",
        timeout: "3500",
      });
      return;
    }
    setCompleteOpen(true);
  };

  const confirmMarkAsCompleted = async () => {
    try {
      const result = await completeInvite({ inviteId }).unwrap();
      setCompleteOpen(false);
      setSelectedIds([]);
      displayAlert({
        open: true,
        message:
          result?.message ||
          `Event completed${
            result?.data?.completedEmployees
              ? ` (${result.data.completedEmployees} employees)`
              : ""
          }`,
        type: "success",
        timeout: "2500",
      });
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to complete event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const canUseRowActions = isInviteSent && Boolean(menuEmployee);
  const currentRowStatus = menuEmployee?.status;
  const rowActions = getPromotionRowActions(
    currentRowStatus,
    markAsEventContext
  );

  if (isLoading) {
    return <ProgressBar />;
  }

  if (isError || !invite) {
    return (
      <div className="promotions-history-shell container">
        <BreadcrumbAdmin
          base={[
            {
              _id: "manage-events",
              link: manageEventsPath,
              label: "Manage Events",
            },
          ]}
        />
        <p className="promotions-table__empty">Promotion event not found</p>
      </div>
    );
  }

  const colSpan = isClosed ? 5 : showResponseColumns ? 6 : 4;
  const showActionColumn = !isClosed;

  return (
    <div
      className={`promo-event-detail container${
        isClosed ? " promo-event-detail--closed" : ""
      }${isCreated ? " promo-event-detail--created" : ""}`}
    >
      <BreadcrumbAdmin
        base={[
          {
            _id: "manage-events",
            link: manageEventsPath,
            label: "Manage Events",
          },
          {
            _id: "history-tab",
            link: eventTabPath,
            label: detailTabLabel,
          },
        ]}
        page={invite.name}
      />

      <div className="promo-event-detail__summary">
        <div className="promo-event-detail__summary-head">
          <div>
            <h1 className="promo-event-detail__title">{invite.name}</h1>
            <p
              className="promo-event-detail__created"
              title={
                getDescriptionText(invite.description).length >
                DESCRIPTION_PREVIEW_LIMIT
                  ? getDescriptionText(invite.description)
                  : undefined
              }
            >
              {descriptionPreview(invite.description)}
            </p>
          </div>
          <div className="promo-event-detail__header-actions">
            <span
              className={`promo-pill ${
                detailTab === "ACTION_NEEDED"
                  ? "promo-pill--action-needed"
                  : EVENT_STATUS_PILL_CLASS[invite.status] || ""
              }`}
            >
              {detailTabLabel}
            </span>
            <IconButton
              className="promo-event-detail__header-menu"
              size="small"
              aria-label="Event actions"
              onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          </div>
        </div>

        <div className="promo-event-detail__meta">
          <div>
            <span className="promo-event-detail__meta-label">Book</span>
            <span className="promo-event-detail__meta-value">
              {invite.book?.name || "—"}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Event ID</span>
            <span className="promo-event-detail__meta-value">
              {invite.eventId || "—"}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Created On</span>
            <span className="promo-event-detail__meta-value">
              {formatUtcEventDate(invite.createdOn)}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Organiser</span>
            <span className="promo-event-detail__meta-value">
              {invite.createdBy?.name || "—"}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Venue Address</span>
            <span className="promo-event-detail__meta-value">
              {invite.venue || "—"}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Location URL</span>
            <span className="promo-event-detail__meta-value">
              {invite.locationUrl ? (
                <a
                  href={invite.locationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {invite.locationUrl}
                </a>
              ) : (
                "—"
              )}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Promotion Date</span>
            <span className="promo-event-detail__meta-value">
              {formatUtcEventDate(invite.eventDate)}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">Time</span>
            <span className="promo-event-detail__meta-value">
              {invite.hasEventTime === false
                ? "—"
                : formatPromotionEventTime(invite.eventDate)}
            </span>
          </div>
          <div>
            <span className="promo-event-detail__meta-label">
              No. Of Employees
            </span>
            <span className="promo-event-detail__meta-value">
              {invite.employeeCount ?? allEmployees.length}
            </span>
          </div>
        </div>
      </div>

      <div className="promo-event-detail__employees-head">
        <h2>
          Employees (
          {isClosed
            ? searchQuery
              ? `${closedVisibleFiltered} of ${closedVisibleTotal}`
              : closedVisibleTotal
            : searchQuery
              ? `${employees.length} of ${allEmployees.length}`
              : allEmployees.length}
          )
        </h2>
        <div className="promo-event-detail__employees-actions">
          <Search
            placehold="Search..."
            setSearchVal={(val) => setDebouncedSearch(val)}
          />
          {!isClosed && (
            <Button
              variant="contained"
              className="promotions-page__btn-create-invite"
              startIcon={<AddIcon />}
              disabled={hasPromotionEventElapsed(invite?.eventDate, new Date(), {
                hasEventTime: invite?.hasEventTime !== false,
              })}
              onClick={() => setAddEmployeesOpen(true)}
            >
              Add Employee
            </Button>
          )}
        </div>
      </div>

      <div
        className="promotions-page__table-wrap"
        ref={tableWrapRef}
        style={
          isCreated && rosterScrollMax != null
            ? { maxHeight: rosterScrollMax }
            : undefined
        }
      >
        {(isFetching || isUpdatingStatus || isReminding) && <ProgressBar />}
        <table className="promotions-table promo-event-detail__employees-table">
          <colgroup>
            <col className="promo-event-detail__col-employee" />
            <col className="promo-event-detail__col-company" />
            <col className="promo-event-detail__col-date" />
            {showResponseColumns ? (
              <>
                <col className="promo-event-detail__col-date" />
                <col className="promo-event-detail__col-date" />
              </>
            ) : null}
            {showActionColumn ? (
              <col className="promo-event-detail__col-action" />
            ) : null}
          </colgroup>
          <thead>
            <tr>
              <th
                className={
                  isClosed
                    ? "promo-event-detail__employee-head--closed"
                    : undefined
                }
              >
                {!isClosed && (
                  <Checkbox
                    size="small"
                    checked={selectionModel.isAllMatchingSelected}
                    indeterminate={selectionModel.isSomeMatchingSelected}
                    onChange={handleSelectAllMatching}
                    disabled={selectionModel.headerDisabled}
                    sx={SELECT_CHECKBOX_SX}
                  />
                )}
                {isClosed ? "Employee" : "Select Employee"}
              </th>
              <th>Company</th>
              <th>Completed On</th>
              {showResponseColumns && <th>Contacted On</th>}
              {showResponseColumns && <th>Responded On</th>}
              {showActionColumn ? <th>Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {showResponseColumns ? (
              employeeGroups.map((group, groupIndex) => {
                const isExpanded = expandedStatus === group.status;
                const isRejectedGroup =
                  group.status === PROMOTION_STATUSES.INVITE_REJECTED;
                // Closed events have no Action column. A trailing cell here
                // would shift Rejected rows off the header.
                const showInnerTrailingCol = !isClosed;
                const showInnerKebab = showInnerTrailingCol && !isRejectedGroup;
                const isBelowOpen =
                  pinFollowingHeaders &&
                  expandedGroupIndex >= 0 &&
                  groupIndex > expandedGroupIndex;
                const stickBottom = isBelowOpen
                  ? ACCORDION_END_PADDING +
                    (employeeGroups.length - 1 - groupIndex) *
                      ACCORDION_HEADER_HEIGHT
                  : null;
                return (
                  <React.Fragment key={group.status}>
                    <tr
                      className={`promo-event-detail__status-group${
                        isExpanded
                          ? " promo-event-detail__status-group--open"
                          : ""
                      }${
                        isBelowOpen
                          ? " promo-event-detail__status-group--stick"
                          : ""
                      }`}
                      style={
                        stickBottom == null
                          ? undefined
                          : { "--promo-accordion-stick-bottom": `${stickBottom}px` }
                      }
                    >
                      <td colSpan={colSpan}>
                        <button
                          type="button"
                          className="promo-event-detail__status-group-btn"
                          onClick={() => toggleStatusGroup(group.status)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <KeyboardArrowDownIcon
                              className="promo-event-detail__status-chevron"
                              fontSize="small"
                            />
                          ) : (
                            <KeyboardArrowRightIcon
                              className="promo-event-detail__status-chevron"
                              fontSize="small"
                            />
                          )}
                          <span
                            className={`promo-pill ${
                              STATUS_PILL_CLASS[group.status] || ""
                            }`}
                          >
                            {group.label}
                          </span>
                          <span
                            className={`promo-event-detail__status-count promo-event-detail__status-count--${
                              group.status
                                ? String(group.status)
                                    .toLowerCase()
                                    .replace(/_/g, "-")
                                : "unknown"
                            }`}
                          >
                            {group.rows.length}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className="promo-event-detail__status-body">
                        <td colSpan={colSpan}>
                          {group.rows.length === 0 ? (
                            <p className="promo-event-detail__status-empty">
                              There is no data in this section.
                            </p>
                          ) : (
                          <div
                            className="promo-event-detail__status-scroll"
                            style={
                              accordionScrollMax == null
                                ? undefined
                                : {
                                    height: accordionScrollMax,
                                    maxHeight: accordionScrollMax,
                                  }
                            }
                          >
                            <table className="promo-event-detail__status-inner-table">
                              <colgroup>
                                <col className="promo-event-detail__col-employee" />
                                <col className="promo-event-detail__col-company" />
                                <col className="promo-event-detail__col-date" />
                                <col className="promo-event-detail__col-date" />
                                <col className="promo-event-detail__col-date" />
                                {showInnerTrailingCol ? (
                                  <col className="promo-event-detail__col-action" />
                                ) : null}
                              </colgroup>
                              <tbody>
                                {group.rows.map((row) => {
                                  const showRowCheckbox =
                                    !isClosed &&
                                    isSelectableOnInviteSentEvent(group.status);
                                  const isSelectable = isPromotionRowSelectable(
                                    row.status,
                                    selectionModel
                                  );
                                  const showCommentBubble =
                                    row.status ===
                                      PROMOTION_STATUSES.INVITE_REJECTED &&
                                    employeeHasRejectionComment(row);
                                  return (
                                    <tr
                                      key={row._id}
                                      className={`promo-event-detail__employee-row${
                                        showRowCheckbox && !isSelectable
                                          ? " promotions-table__row--disabled"
                                          : ""
                                      }`}
                                    >
                                      <td className="promo-event-detail__cell-employee">
                                        <label
                                          className={`promotions-table__employee promo-event-detail__employee-label${
                                            showRowCheckbox
                                              ? ""
                                              : " promo-event-detail__employee-label--static"
                                          }`}
                                        >
                                          {showRowCheckbox && (
                                            <Checkbox
                                              size="small"
                                              checked={selectionModel.visibleSelectedIds.includes(
                                                row._id
                                              )}
                                              disabled={!isSelectable}
                                              onChange={(e) =>
                                                toggleOne(row, e.target.checked)
                                              }
                                              sx={SELECT_CHECKBOX_SX}
                                            />
                                          )}
                                          <span className="promo-event-detail__employee-name">
                                            {row.employee?.name || "—"}
                                          </span>
                                        </label>
                                      </td>
                                      <td className="promo-event-detail__cell-muted">
                                        {row.company?.name || "—"}
                                      </td>
                                      <td className="promo-event-detail__cell-muted">
                                        {formatShortDate(row.completedOn)}
                                      </td>
                                      <td className="promo-event-detail__cell-muted">
                                        {formatShortDate(row.contactedOn)}
                                      </td>
                                      <td className="promo-event-detail__cell-muted">
                                        {isClosed && showCommentBubble ? (
                                          <div className="promo-event-detail__responded-with-comment">
                                            <span>
                                              {formatShortDate(row.respondedOn)}
                                            </span>
                                            <RejectionCommentButton
                                              onClick={() =>
                                                openRejectionComment(row)
                                              }
                                            />
                                          </div>
                                        ) : (
                                          formatShortDate(row.respondedOn)
                                        )}
                                      </td>
                                      {showInnerTrailingCol ? (
                                        <td className="promo-event-detail__cell-action">
                                          <div className="promo-event-detail__row-actions">
                                            {showCommentBubble ? (
                                              <RejectionCommentButton
                                                onClick={() =>
                                                  openRejectionComment(row)
                                                }
                                              />
                                            ) : null}
                                            {showInnerKebab ? (
                                              <IconButton
                                                size="small"
                                                aria-label="Employee actions"
                                                onClick={(e) =>
                                                  openEmployeeMenu(e, row)
                                                }
                                                disabled={
                                                  isClosed ||
                                                  isUpdatingStatus ||
                                                  isReminding
                                                }
                                              >
                                                <MoreVertIcon fontSize="small" />
                                              </IconButton>
                                            ) : null}
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="promotions-table__empty">
                  {searchQuery
                    ? "No matching employees"
                    : "No employees on this event"}
                </td>
              </tr>
            ) : (
              employees.map((row) => {
                const isSelectable = isPromotionRowSelectable(
                  row.status,
                  selectionModel
                );
                const isChecked =
                  selectionModel.visibleSelectedIds.includes(row._id);
                return (
                  <tr
                    key={row._id}
                    className={
                      isSelectable
                        ? undefined
                        : "promotions-table__row--disabled"
                    }
                  >
                    <td>
                      <label className="promotions-table__employee">
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          disabled={!isSelectable}
                          onChange={(e) => toggleOne(row, e.target.checked)}
                          sx={SELECT_CHECKBOX_SX}
                        />
                        <span>{row.employee?.name || "—"}</span>
                      </label>
                    </td>
                    <td>{row.company?.name || "—"}</td>
                    <td>{formatShortDate(row.completedOn)}</td>
                    <td>
                      <div className="promo-event-detail__row-actions">
                        <IconButton
                          size="small"
                          aria-label="Remove employee"
                          onClick={() => requestRemoveEmployees([row._id])}
                          disabled={isRemoving || isLocked}
                          sx={{ color: "#d92d20" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showEventToast && (
        <Portal>
          <Snackbar
            open={!selectionToastHidden}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            className="promotions-selection-toast promotions-selection-toast--manage"
            sx={{
              "--promo-toast-center": toastBox
                ? `${toastBox.center}px`
                : undefined,
              "--promo-toast-min-width": toastBox
                ? `${toastBox.minWidth}px`
                : undefined,
              "--promo-toast-max-width": toastBox
                ? `${toastBox.maxWidth}px`
                : undefined,
            }}
          >
            <Paper
              elevation={0}
              className="promotions-selection-toast__content"
              role="status"
            >
              <span className="promotions-selection-toast__count">
                ({selectedCount}) Selected
              </span>
              <div className="promotions-selection-toast__actions">
                <Button
                  className="promotions-page__btn-reset"
                  variant="contained"
                  disableElevation
                  onClick={() => setSelectedIds([])}
                >
                  Reset
                </Button>
                {isInviteSent ? (
                  <>
                    <Button
                      className="promotions-page__btn-complete"
                      variant="contained"
                      disableElevation
                      onClick={handleMarkAsCompleted}
                      disabled={isCompleting || !canCompleteEvent}
                      title={completeDisabledReason}
                    >
                      {isCompleting
                        ? "Completing…"
                        : "Mark as Event Completed"}
                    </Button>
                    <Button
                      className="promotions-page__btn-mark"
                      variant="contained"
                      disableElevation
                      onClick={(e) =>
                        setMarkAsMenuAnchor((current) =>
                          current ? null : e.currentTarget
                        )
                      }
                      disabled={!canMarkAs}
                      endIcon={<KeyboardArrowDownIcon />}
                      aria-label="Mark As"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(markAsMenuAnchor)}
                    >
                      {isUpdatingStatus ? "Updating…" : "Mark As"}
                    </Button>
                  </>
                ) : selectedCount > 0 ? (
                  <Button
                    className="promotions-page__btn-remove"
                    variant="contained"
                    disableElevation
                    onClick={() =>
                      requestRemoveEmployees(selectionModel.visibleSelectedIds)
                    }
                    disabled={isRemoving}
                  >
                    {isRemoving ? "Removing…" : "Remove Selected"}
                  </Button>
                ) : (
                  <Button
                    className="promotions-page__btn-create-invite"
                    variant="contained"
                    disableElevation
                    onClick={handleSendInviteClick}
                    disabled={isSending || !allEmployees.length}
                  >
                    {isSending ? "Sending…" : "Send Invite"}
                  </Button>
                )}
              </div>
            </Paper>
          </Snackbar>
        </Portal>
      )}

      <Menu
        anchorEl={markAsMenuAnchor}
        open={Boolean(markAsMenuAnchor)}
        onClose={closeMarkAsMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: 1500 }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        {markAsActions.length ? (
          markAsActions.map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => requestMarkAs(action)}
              disabled={isUpdatingStatus}
            >
              {action.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No status changes for this selection</MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={closeHeaderMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        {isCreated && (
          <MenuItem onClick={handleHeaderEdit}>Edit Event</MenuItem>
        )}
        {(isCreated || isInviteSent || isClosed) && (
          <MenuItem onClick={handleHeaderViewEvent}>Preview Email</MenuItem>
        )}
        {canCancelEvent && (
          <MenuItem
            onClick={handleHeaderCancel}
            className="promo-history-actions-menu__danger"
          >
            Cancel Event
          </MenuItem>
        )}
        {isCreated && (
          <MenuItem
            onClick={handleHeaderDelete}
            className="promo-history-actions-menu__danger"
          >
            Delete Event
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeEmployeeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        {rowActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => handleRowAction(action)}
            disabled={
              !canUseRowActions ||
              isUpdatingStatus ||
              (action.type !== "side" &&
                !isPromotionMarkAsActionEnabled(action, markAsEventContext))
            }
            title={
              action.requiresEventElapsed &&
              !isPromotionMarkAsActionEnabled(action, markAsEventContext)
                ? "Available after the event time"
                : undefined
            }
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>

      <AddEmployeesToEventModal
        open={addEmployeesOpen}
        onClose={() => setAddEmployeesOpen(false)}
        inviteId={inviteId}
        sendImmediately={isInviteSent}
        onSuccess={() => {
          refetch();
        }}
      />

      <RemoveEmployeesConfirmModal
        open={removeConfirmOpen}
        onClose={() => {
          if (isRemoving) return;
          setRemoveConfirmOpen(false);
          setPendingRemoveIds([]);
        }}
        onConfirm={confirmRemoveEmployees}
        confirming={isRemoving}
        removeCount={pendingRemoveIds.length}
      />

      <SendInviteConfirmModal
        open={sendInviteOpen}
        onClose={() => {
          if (isSending) return;
          setSendInviteOpen(false);
        }}
        onConfirm={confirmSendInvite}
        confirming={isSending}
        recipients={inviteRecipientEmails}
      />

      <EditPromotionInviteModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setMissingSendFields([]);
        }}
        invite={invite}
        missingSendFields={missingSendFields}
        onSuccess={() => refetch()}
      />

      <CancelEventConfirmModal
        open={cancelOpen}
        onClose={() => {
          if (isCancelling) return;
          setCancelOpen(false);
        }}
        onConfirm={confirmCancelEvent}
        confirming={isCancelling}
        recipients={cancelRecipientEmails}
      />

      <CompleteEventConfirmModal
        open={completeOpen}
        onClose={() => {
          if (isCompleting) return;
          setCompleteOpen(false);
        }}
        onConfirm={confirmMarkAsCompleted}
        confirming={isCompleting}
      />

      <DeleteEventConfirmModal
        open={deleteOpen}
        onClose={() => {
          if (isDeleting) return;
          setDeleteOpen(false);
        }}
        onConfirm={confirmDeleteEvent}
        confirming={isDeleting}
      />

      <RejectInviteReasonModal
        open={rejectOpen}
        onClose={() => {
          if (isUpdatingStatus) return;
          setRejectOpen(false);
          setRejectTarget(null);
          setPendingMarkAsAction(null);
        }}
        onConfirm={confirmRejectInvite}
        confirming={isUpdatingStatus}
        employeeName={
          rejectTarget?.employee?.name ||
          (selectedCount === 1
            ? employees.find(
                (row) => row._id === selectionModel.visibleSelectedIds[0]
              )?.employee?.name || ""
            : "")
        }
        count={pendingMarkAsAction ? selectedCount : 1}
      />

      <RejectionCommentModal
        open={viewReasonOpen}
        onClose={() => {
          setViewReasonOpen(false);
          setRejectionComment(null);
        }}
        comment={rejectionComment}
      />

      <EmailTemplatePreviewModal
        open={Boolean(invitePreview) || isPreviewingInvite}
        onClose={() => setInvitePreview(null)}
        title={`Preview Email : ${invite?.name || "Event"}`}
        subject={invitePreview?.subject}
        html={invitePreview?.html}
        loading={isPreviewingInvite && !invitePreview}
      />
    </div>
  );
};
